import React from "react";

/**
 * Presentation primitives the dashboard-style pages were missing.
 *
 * The app's default surface is a flat white card, which is right for dense
 * financial tables but leaves persuasive pages (partner earnings, tier progress)
 * looking like a spreadsheet. These add hierarchy without inventing a second design
 * language: same radii, same spacing scale, same brand colour — just used with more
 * contrast where a page needs to sell rather than report.
 */

/** Full-bleed gradient header for pages that open an argument rather than a table. */
export function HeroPanel({ eyebrow, title, subtitle, children, className = "", testid }) {
  return (
    <section
      data-testid={testid}
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 via-brand-600 to-indigo-700 text-white p-6 sm:p-9 ${className}`}
    >
      {/* Soft light source, kept subtle so text contrast never drops. */}
      <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-10 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
      <div className="relative">
        {eyebrow && (
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/70">
            {eyebrow}
          </span>
        )}
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-2">{title}</h1>
        {subtitle && <p className="text-white/80 mt-2 max-w-2xl leading-relaxed">{subtitle}</p>}
        {children && <div className="mt-6">{children}</div>}
      </div>
    </section>
  );
}

/** A single number with its label — reads as a figure, not a table cell. */
export function Metric({ label, value, sub, tone = "default", testid }) {
  const toneCls = {
    default: "text-slate-900",
    brand: "text-brand-600",
    positive: "text-emerald-600",
    negative: "text-rose-600",
    inverse: "text-white",
  }[tone];
  const labelCls = tone === "inverse" ? "text-white/60" : "text-slate-400";
  return (
    <div data-testid={testid}>
      <div className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${labelCls}`}>{label}</div>
      <div className={`mt-1.5 text-2xl sm:text-3xl font-heading font-semibold tracking-tight tabular ${toneCls}`}>
        {value}
      </div>
      {sub && <div className={`text-xs mt-0.5 ${tone === "inverse" ? "text-white/60" : "text-slate-400"}`}>{sub}</div>}
    </div>
  );
}

/** Progress toward a threshold. Used for tier advancement. */
export function ProgressBar({ pct = 0, tone = "brand", className = "" }) {
  const bar = { brand: "bg-brand-500", gold: "bg-yellow-400", emerald: "bg-emerald-500" }[tone] || "bg-brand-500";
  return (
    <div className={`h-2 rounded-full bg-slate-100 overflow-hidden ${className}`}>
      <div className={`h-full rounded-full ${bar} transition-[width] duration-500`}
           style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

/** Numbered step, for explaining a process in order. */
export function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-7 h-7 shrink-0 rounded-full bg-brand-50 text-brand-600 grid place-items-center text-xs font-semibold">
        {n}
      </div>
      <div className="min-w-0 pb-6">
        <div className="font-medium text-slate-900">{title}</div>
        <div className="text-sm text-slate-600 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

/** Comparison row used by the tier table — highlights the viewer's current row. */
export function TierRow({ tier, label, sharePct, perSub, current, children }) {
  return (
    <div
      data-testid={`tier-row-${tier}`}
      className={`grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-3 rounded-xl transition-colors ${
        current ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {children}
        <span className={`font-medium ${current ? "text-brand-700" : "text-slate-700"}`}>{label}</span>
      </div>
      <span className="text-sm text-slate-500 tabular">{sharePct}%</span>
      <span className="text-sm font-semibold tabular text-slate-900">{perSub}</span>
    </div>
  );
}
